/**
 * LiveAvatarAdModal — small, NON-BLOCKING post-login promo card for the
 * Live Avatar feature. Shown once per browser session to ALL logged-in
 * users.
 *
 * Presentation (2026-06-22 relocate): a fixed bottom card (toast/callout
 * style) — NOT a centered modal. There is NO backdrop and NO focus trap,
 * so the entire site stays clickable and usable while the card is open.
 * Truly non-blocking.
 *
 * Position vs. the Setup Guide dock (src/components/setup/SetupTour.tsx,
 * which sits bottom-RIGHT on desktop `sm:right-4 sm:bottom-24 sm:w-80`, and
 * a full-width strip at `left-2 right-2 bottom-24` on mobile, and which
 * EXPANDS UPWARD — its checklist grows above the `bottom-24` (96px) anchor):
 *  - Desktop: this card is RIGHT-aligned (`sm:right-4 sm:left-auto
 *    sm:w-80`) and STACKED ABOVE the dock at `sm:bottom-[28rem]` (448px).
 *    The dock's fully-expanded top sits at ~396px (96px anchor + ~300px of
 *    expanded checklist on the narrow 320px-wide card), so 448px clears it
 *    with a ~52px gap in BOTH states (collapsed launcher AND fully expanded).
 *    Right-aligned keeps it clear of the left-fixed sidebar entirely (the
 *    prior bottom-LEFT placement tucked under the z-50 sidebar). When the
 *    dock is absent (non-Shopify build or dismissed) the card just floats
 *    above an empty bottom-right slot.
 *  - Mobile: full-width card raised to `bottom-[25rem]` (400px) so it stacks
 *    ABOVE the dock's fully-expanded top (~376px = 96px anchor + ~280px of
 *    expanded checklist on the wider full-width strip) rather than
 *    overlapping it.
 *
 * Behaviour (unchanged from the original modal):
 *  - Renders null when `APP_CONFIG.liveAvatarArticleUrl`
 *    (VITE_LIVE_AVATAR_ARTICLE_URL) is empty/undefined → feature off until
 *    the user configures the article URL.
 *  - Renders null when the sessionStorage flag is already set → once per
 *    session; returns next session (no permanent suppression).
 *  - Dismiss via X. "Learn more" opens the article in a NEW TAB
 *    (target="_blank" rel="noopener noreferrer"); it does NOT navigate the
 *    app away. Dismissing or clicking through sets the session flag.
 *  - ESC also dismisses (convenience; the card is non-blocking either way).
 *
 * Mounted at the layout shell (Layout.tsx) so it shows post-login
 * regardless of landing route. No plan gate — audience is all users.
 */
import React from 'react';
import { Sparkles, X } from 'lucide-react';
import { APP_CONFIG } from '../config/appConfig';

const IS_SHOPIFY_BUILD = APP_CONFIG.billingProvider === 'shopify';

const SESSION_KEY = 'la_ad_seen';

function alreadySeen(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    // sessionStorage unavailable (private mode / disabled) — treat as
    // not-seen; without persistence the card simply shows once per page
    // load, which is acceptable.
    return false;
  }
}

function markSeen(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // ignore — see alreadySeen()
  }
}

const LiveAvatarAdModal: React.FC = () => {
  const articleUrl = APP_CONFIG.liveAvatarArticleUrl;
  // Initialise hidden if the env var is unset OR the session flag is
  // already set. Computed once on mount — the value is build-time / read
  // synchronously so there's no async race.
  const [open, setOpen] = React.useState<boolean>(
    () => Boolean(articleUrl) && !alreadySeen(),
  );

  const dismiss = React.useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);

  // ESC closes (purely a convenience — the card never blocks the page).
  // Only bound while open.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  if (!articleUrl || !open) return null;

  return (
    // Fixed bottom card — NO backdrop, NO inset-0 wrapper, so nothing
    // intercepts clicks elsewhere on the page. Mobile: full-width strip
    // raised to bottom-[25rem] (400px) to clear the Setup Guide dock's
    // fully-expanded top (~376px). Desktop: RIGHT-aligned (clear of the
    // left-fixed z-50 sidebar) and stacked at bottom-[28rem] (448px) ABOVE
    // the bottom-right dock (expanded top ~396px) so it never collides with
    // the dock collapsed OR fully expanded. z-40 (under sidebar).
    <div
      className="fixed z-40 left-2 right-2 bottom-[25rem] sm:left-auto sm:right-4 sm:bottom-[28rem] sm:w-80 sm:max-w-sm"
      role="dialog"
      aria-modal="false"
      aria-labelledby="la-ad-title"
    >
      <div className="relative rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2.5 right-2.5 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-4 py-4 pr-9">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 text-white shrink-0">
              <Sparkles className="w-4 h-4" />
            </span>
            <h3
              id="la-ad-title"
              className="text-base font-bold text-gray-900 dark:text-gray-50"
            >
              Only on AskProdigy: Live Avatar
            </h3>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
            AskProdigy is the only customer-support platform that gives your
            voice agent a realistic, face-to-face video avatar — your
            shoppers see and talk to a real agent, not just a chat bubble.
            {' '}
            {IS_SHOPIFY_BUILD
              ? 'Available on the Business plan.'
              : 'Available as an add-on — upgrade in Billing.'}
          </p>

          <div className="flex items-center gap-3">
            <a
              href={articleUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={dismiss}
              className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-semibold rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm transition-colors"
            >
              Learn more
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAvatarAdModal;
